import { LoadingPage } from "$components/loading/loading-page";
import NavigateHome from "$routes/_lib/navigate-home";
import { isApolloError } from "@apollo/client";
import { OAuthError, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Auth0ErrorPage } from "./_lib/auth0-error-page";

const isKnownAuth0Error = (message: string) =>
  message.includes("Timeout when executing") ||
  message.includes("Invalid state");

export const Auth0CallbackPage: FC = () => {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0();

  // fetch the id token from auth0
  useEffect(() => {
    if (isLoading === false && isAuthenticated === false && !error) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect]);

  useEffect(() => {
    if (error && !isKnownAuth0Error(error.message)) {
      // Then it's an unexpected error message so let's send it to Sentry
      Sentry.captureException(error);
    }
  }, [error]);

  if (!isLoading && isAuthenticated) {
    return <NavigateHome />; // TODO: are we doing this correctly?
  }

  // Some times authError can exist but the user is authenticated, in which case the error is not relevant
  // This can happen if the users presses the back immediately after logging in. In that case the navigate
  // back to this page and the error will be "Invalid state", because the local state will have been removed
  // from the local auth0 store after the initial login. In this circumstance the user will be redirected back
  // to the application as expected.
  if (error instanceof OAuthError) {
    const errorMessage = error.error_description ?? error.message;
    if (errorMessage === "user is blocked") {
      return (
        <Auth0ErrorPage
          title={
            <FormattedMessage
              id="auth.errorPage.blocked"
              defaultMessage="Your account is blocked"
            />
          }
        />
      );
    }
    if (isKnownAuth0Error(errorMessage)) {
      return (
        <Auth0ErrorPage
          title={
            <FormattedMessage
              id="auth.errorPage.networkIssue.title"
              defaultMessage="Network Connection Issues"
            />
          }
          body={
            <FormattedMessage
              id="auth.errorPage.networkIssue.body"
              defaultMessage="Please check your connection and try refreshing or logging in again by clicking the button below."
            />
          }
        />
      );
    }

    return <Auth0ErrorPage errorDescription={errorMessage} />;
  }

  if (error) {
    // TODO: Is this branch ever accessible considering the error object comes from Auth0?
    if (isApolloError(error) && error?.networkError) {
      return (
        <Auth0ErrorPage
          title={
            <FormattedMessage
              id="auth.errorPage.apolloNetworkError.title"
              defaultMessage="ePOCUS is currently experiencing issues, we apologise for the disruption"
            />
          }
          body={
            <FormattedMessage
              id="auth.errorPage.apolloNetworkError.body"
              defaultMessage="Please try again later or contact your ePOCUS administrator if the issue persists."
            />
          }
        />
      );
    }

    return (
      <Auth0ErrorPage
        title={
          <FormattedMessage
            id="auth.errorPage.missing"
            defaultMessage="Your account is inactive or missing permissions"
          />
        }
      />
    );
  }

  return <LoadingPage />;
};
